/**
 * "Allow negative leave balances" toggle — a leave policy, so it lives under
 * the Settings screen's Leave types tab (rendered by `leave_types_panel.tsx`,
 * directly beneath the "Leave types" heading), NOT on the Payroll tab where it
 * used to render. The value is still stored in
 * `entity_payroll_settings.allow_negative_leave_balance` and read/written via
 * the same `payroll.getPayrollSettings`/`updatePayrollSettings` as the rest of
 * the Payroll tab (`usePayrollSettings()`), so this is a UI relocation only.
 *
 * The flag is a leave policy owned by the Leave types tab, so the Payroll
 * tab's `buildPayrollTabPatch` never carries it — this card is the only
 * surface that writes it.
 *
 * Unlike the Payroll tab's batched save-on-button, this toggle saves
 * immediately on flip with a patch containing ONLY
 * `allow_negative_leave_balance`. The switch is disabled while the save is in
 * flight (so a rapid double-flip can't race two writes); a failed save reverts
 * the optimistic flip and surfaces the shared `payroll_settings.save_error`,
 * and a successful save shows a transient "Saved" confirmation.
 *
 * The Leave types panel already renders its own loading / entitlement /
 * error / no-entity states, so this card renders nothing for `loading`,
 * `no_entity` and `entitlement_required`; for `error` it shows an inline
 * retry so the toggle can still be recovered without reloading the panel.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, GlassCard, Switch } from '@jumpstone/ui-react';
import { usePayrollSettings } from '../../data/use_payroll_settings';

export function LeaveNegativeBalanceCard(): React.JSX.Element | null {
  const { t } = useTranslation('wp');
  const payrollSettings = usePayrollSettings();
  const state = payrollSettings.state;
  const [enabled, setEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (state.status === 'ready') {
      setEnabled(state.settings.allow_negative_leave_balance);
    }
  }, [state]);

  useEffect(
    () => () => {
      if (savedTimer.current !== null) clearTimeout(savedTimer.current);
    },
    [],
  );

  // The Leave types panel owns these states already — rendering them here too
  // would duplicate its loading/entitlement/no-entity messages.
  if (
    state.status === 'loading' ||
    state.status === 'no_entity' ||
    state.status === 'entitlement_required'
  ) {
    return null;
  }

  if (state.status === 'error') {
    return (
      <GlassCard className="rounded-2xl p-5">
        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-start gap-1.5 sm:gap-4">
          <label className="nv-label">
            {t('settings.leave_types.allow_negative_balance')}
          </label>
          <div className="flex flex-col gap-1.5">
            <p className="nv-help is-error mt-0">
              {t('payroll_settings.error_description')}
            </p>
            <div className="flex">
              <Button
                variant="secondary"
                size="sm"
                onClick={payrollSettings.retry}
              >
                {t('payroll_settings.retry')}
              </Button>
            </div>
          </div>
        </div>
      </GlassCard>
    );
  }

  // Captured as a plain local so the handler closes over a stable reference
  // rather than re-reading `payrollSettings.save` (which TypeScript cannot
  // narrow back to non-`undefined` inside the nested async body).
  const save = payrollSettings.save;
  const canWrite = save !== undefined;

  async function handleToggle(next: boolean): Promise<void> {
    if (save === undefined || isSaving) return;
    setEnabled(next);
    setIsSaving(true);
    setSaveFailed(false);
    setSaved(false);
    const result = await save({ allow_negative_leave_balance: next });
    setIsSaving(false);
    if (result.outcome !== 'success') {
      setEnabled(!next);
      setSaveFailed(true);
      return;
    }
    setSaved(true);
    if (savedTimer.current !== null) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => {
      setSaved(false);
    }, 2000);
  }

  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-start gap-1.5 sm:gap-4">
        <label className="nv-label">
          {t('settings.leave_types.allow_negative_balance')}
        </label>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-start gap-3">
            <Switch
              checked={enabled}
              onCheckedChange={(next) => void handleToggle(next)}
              disabled={!canWrite || isSaving}
              aria-label={t('settings.leave_types.allow_negative_balance')}
              data-testid="settings-allow-negative-leave-balance"
            />
            <p className="nv-help mt-0">
              {enabled
                ? t('settings.leave_types.allow_negative_balance_on_hint')
                : t('settings.leave_types.allow_negative_balance_off_hint')}
            </p>
          </div>
          {saved && <p className="nv-help mt-0">{t('settings.saved')}</p>}
          {saveFailed && (
            <p className="nv-help is-error mt-0">
              {t('payroll_settings.save_error')}
            </p>
          )}
        </div>
      </div>
    </GlassCard>
  );
}
